import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase/index.tsx';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconGift } from '@sorare/core/src/atoms/icons/system/iconGift.ts';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { Chip } from '@sorare/core/src/atoms/ui/Chip/index.tsx';
import { ANY_SPORT_SERIES_GUESS_THE_SCORE } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary.ts';
import { isType, isTypes } from '@sorare/core/src/lib/gql/index.ts';

import { getProgress } from '#src/components/series/lineup/GuessTheScoreTaskLineup/GuessTheScoreTaskLineupProgress/index.tsx';

import { VsIcon } from './VsIcon/index.tsx';
import type { GuessTheScoreBadge_taskInterface } from './__generated__/index.graphql.ts';
import badgeBg from './assets/guess-the-score-bg.webp';

type GuessTheScoreBadgeState = 'new' | 'inProgress' | 'claimable';

const Root = styled(ButtonBase)`
  position: relative;
`;
const Circle = styled(Horizontal).attrs({ center: true })`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: url(${badgeBg}) no-repeat center / cover;
  padding: 2px;
  position: relative;
  overflow: hidden;
`;
const PlayerImg = styled.img`
  width: 40px;
  object-fit: cover;
  position: absolute;
  top: 8px;

  &.home {
    left: -8px;
  }
  &.away {
    right: -8px;
  }
`;
const Vs = styled.div`
  position: relative;
  z-index: 1;
  & > * {
    height: 30px;
  }
`;
const ChipWrapper = styled.div`
  position: absolute;
  /* half the Chip height — keeps it centred on the circle edge */
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
`;
const ScorePill = styled(Horizontal).attrs({ center: true })`
  position: relative;
  min-width: 56px;
  height: 22px;
  padding: 2px var(--unit);
  border-radius: 11px;
  background: var(--c-nd-50);
  backdrop-filter: blur(16px);
  color: var(--c-white);
  font: var(--t-label-s);
  font-weight: var(--t-bold);
  overflow: hidden;
  isolation: isolate;
`;
const ScorePillProgress = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: var(--progress);
  border-radius: inherit;
  background: var(--c-gradient-hotstreak);
  z-index: -1;
`;
const ScorePillText = styled.span`
  position: relative;
  z-index: 1;
`;

const getState = (
  task: GuessTheScoreBadge_taskInterface
): GuessTheScoreBadgeState => {
  if (!isType(task, 'GuessTheScoreTask')) {
    return 'new';
  }
  if (task.aasmState === ManagerProgressionTaskState.COMPLETED) {
    return 'claimable';
  }
  if (task.taskLineups?.some(lineup => lineup.hasLiveGames)) {
    return 'inProgress';
  }
  return 'new';
};

const getLiveLineup = (task: GuessTheScoreBadge_taskInterface) => {
  if (!isType(task, 'GuessTheScoreTask')) {
    return null;
  }
  const lineups = task.taskLineups ?? [];
  return lineups.find(lineup => lineup.hasLiveGames) ?? lineups[0] ?? null;
};

const renderBottom = (
  state: GuessTheScoreBadgeState,
  score: number | null,
  target: number | null
) => {
  if (state === 'claimable') {
    return (
      <Chip color="purple">
        <FontAwesomeIcon icon={iconGift} />
      </Chip>
    );
  }
  if (state === 'inProgress') {
    const safeScore = score ?? 0;
    return (
      <ScorePill
        style={
          {
            '--progress': `${getProgress(score, target) * 100}%`,
          } as CSSProperties
        }
      >
        <ScorePillProgress />
        <ScorePillText>{Math.round(safeScore)}</ScorePillText>
      </ScorePill>
    );
  }
  return (
    <Chip color="glow">
      <FormattedMessage {...glossary.new} />
    </Chip>
  );
};

type Props = {
  task: GuessTheScoreBadge_taskInterface;
};

export const GuessTheScoreBadge = ({ task }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const game = isType(task, 'GuessTheScoreTask') ? task.game : null;

  const homeTeam = isTypes(game?.homeTeam, ['Club', 'NationalTeam'])
    ? game?.homeTeam
    : null;
  const awayTeam = isTypes(game?.awayTeam, ['Club', 'NationalTeam'])
    ? game?.awayTeam
    : null;
  const homeTopPlayer = homeTeam?.topPlayers?.[0] ?? null;
  const awayTopPlayer = awayTeam?.topPlayers?.[0] ?? null;
  const state = getState(task);
  const liveLineup = getLiveLineup(task);
  const liveScore = liveLineup?.score ?? null;
  const target = liveLineup?.targetScore ?? null;

  return (
    <Root to={generateSportContextPath(ANY_SPORT_SERIES_GUESS_THE_SCORE)}>
      <Circle>
        {!!homeTopPlayer && (
          <PlayerImg
            className="home"
            src={homeTopPlayer.squaredPictureUrl}
            alt=""
            role="presentation"
          />
        )}
        {!!awayTopPlayer && (
          <PlayerImg
            className="away"
            src={awayTopPlayer.squaredPictureUrl}
            alt=""
            role="presentation"
          />
        )}
        <Vs>
          <VsIcon />
        </Vs>
      </Circle>
      <ChipWrapper>{renderBottom(state, liveScore, target)}</ChipWrapper>
    </Root>
  );
};

GuessTheScoreBadge.fragments = {
  taskInterface: gql`
    fragment GuessTheScoreBadge_taskInterface on TaskInterface {
      id
      ... on GuessTheScoreTask {
        id
        aasmState
        taskLineups {
          id
          hasLiveGames
          score
          targetScore
        }
        game {
          id
          homeTeam {
            slug
            ... on Club {
              slug
              topPlayers {
                slug
                squaredPictureUrl
              }
            }
            ... on NationalTeam {
              slug
              topPlayers {
                slug
                squaredPictureUrl
              }
            }
          }
          awayTeam {
            slug
            ... on Club {
              slug
              topPlayers {
                slug
                squaredPictureUrl
              }
            }
            ... on NationalTeam {
              slug
              topPlayers {
                slug
                squaredPictureUrl
              }
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<GuessTheScoreBadge_taskInterface>,
};
